import { ErrorHandler, inject, PLATFORM_ID } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

const RELOAD_TIMEOUT_MS = 60 * 1000; // 1 minute
const RELOAD_STORAGE_KEY = 'lastReload';

/**
 * Module loading error patterns that trigger auto-reload
 * These typically occur after deployments when users have stale chunks
 * @see https://sentry.io/answers/failed-to-fetch-dynamically-imported-module-in-react/
 */
const MODULE_LOADING_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  '\'text/html\' is not a valid JavaScript MIME type',
  'Importing a module script failed',
  'error loading dynamically imported module',
] as const;

/**
 * Extracts error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

/**
 * Checks if error is a module loading error that should trigger reload
 */
function isModuleLoadingError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return MODULE_LOADING_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/**
 * Checks if enough time has passed since last reload to prevent reload loops
 * SSR-safe: only call this in browser context
 */
function shouldAttemptReload(): boolean {
  if (typeof localStorage === 'undefined') {
    return false; // SSR environment, don't attempt reload
  }

  const lastReload = localStorage.getItem(RELOAD_STORAGE_KEY);
  if (!lastReload) {
    return true;
  }

  const timeSinceLastReload = Date.now() - parseInt(lastReload, 10);
  return timeSinceLastReload > RELOAD_TIMEOUT_MS;
}

/**
 * Performs page reload and records timestamp to prevent loops
 * SSR-safe: only call this in browser context
 */
function reloadPage(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(RELOAD_STORAGE_KEY, Date.now().toString());
  }

  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

/**
 * Application-wide error handler
 *
 * Features:
 * - Auto-reloads on module loading errors (fixes stale chunks after deployments)
 * - Reports all errors to Sentry for monitoring
 * - SSR-safe with proper browser environment checks
 *
 * Only active in production and browser environments
 */
export class AppErrorHandler implements ErrorHandler {
  private readonly platformId = inject(PLATFORM_ID);

  handleError(error: unknown): void {
    // Handle module loading errors with auto-reload (browser only)
    if (isPlatformBrowser(this.platformId) && isModuleLoadingError(error)) {
      if (shouldAttemptReload()) {
        reloadPage();
        return;
      }
    }

    // Report to Sentry in production environments
    if (environment.production) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorToCapture = (error as any)?.originalError || error;
      Sentry.captureException(errorToCapture);
    }

    // Re-throw to maintain default error handling behavior
    throw error;
  }
}
